import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Phone, Trash2, Calendar, Clock, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS = {
  new: { label: "E re", cls: "bg-accent/12 text-accent border-accent/30" },
  contacted: { label: "Kontaktuar", cls: "bg-foreground/8 text-foreground border-border" },
  done: { label: "Përfunduar", cls: "bg-muted text-muted-foreground border-border" },
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings-list.php");
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ff_token");
        nav("/admin/login");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("ff_token")) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings-update.php/${id}`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings-delete.php/${id}`);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Rezervimi u fshi.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem("ff_token");
    nav("/admin/login");
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = {
    all: bookings.length,
    new: bookings.filter((b) => b.status === "new").length,
    contacted: bookings.filter((b) => b.status === "contacted").length,
    done: bookings.filter((b) => b.status === "done").length,
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container-x h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="FizioFit Logo" className="h-9 w-auto object-contain" />
            <span className="font-medium tracking-tight text-foreground">FizioFit · Rezervimet</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="h-10 px-4 inline-flex items-center gap-2 rounded-sm border border-border text-sm text-foreground hover:bg-secondary transition-colors">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Rifresko
            </button>
            <button data-testid="admin-logout" onClick={logout} className="h-10 px-4 inline-flex items-center gap-2 rounded-sm bg-foreground text-primary-foreground text-sm hover:bg-accent transition-colors">
              <LogOut size={15} /> Dil
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {["all", "new", "contacted", "done"].map((k) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`h-9 px-4 rounded-sm text-sm border transition-all ${
                filter === k ? "bg-accent text-white border-accent shadow-sm" : "border-border text-muted-foreground hover:text-foreground hover:border-accent/50"
              }`}
            >
              {k === "all" ? "Të gjitha" : STATUS[k].label} ({counts[k]})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-muted-foreground">Duke ngarkuar...</p>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-empty" className="flat-card p-16 text-center">
            <Inbox size={32} className="mx-auto text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">Nuk ka rezervime për të shfaqur.</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {filtered.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="flat-card p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium tracking-tight text-foreground">{b.first_name} {b.last_name}</h3>
                    <a href={`tel:${b.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
                      <Phone size={13} /> {b.phone}
                    </a>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-sm border ${STATUS[b.status]?.cls || STATUS.new.cls}`}>
                    {STATUS[b.status]?.label || b.status}
                  </span>
                </div>

                <div className="mt-4 text-sm text-foreground">{b.service}</div>
                {(b.date || b.time) && (
                  <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                    {b.date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {b.date}</span>}
                    {b.time && <span className="inline-flex items-center gap-1"><Clock size={12} /> {b.time}</span>}
                  </div>
                )}
                {b.message && <p className="mt-3 text-sm text-muted-foreground leading-relaxed border-l-2 border-border pl-3">{b.message}</p>}

                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                  <div className="flex gap-1.5">
                    {Object.keys(STATUS).map((s) => (
                      <button
                        key={s}
                        data-testid={`set-status-${s}-${b.id}`}
                        onClick={() => setStatus(b.id, s)}
                        className={`text-xs px-2.5 py-1.5 rounded-sm border transition-colors ${
                          b.status === s ? "bg-foreground text-primary-foreground border-foreground" : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {STATUS[s].label}
                      </button>
                    ))}
                  </div>
                  <button data-testid={`delete-${b.id}`} onClick={() => remove(b.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
